"use client"

import { useEffect, useState } from "react"
import API from "../services/api"
import CompressedFileInput from "./CompressedFileInput"

const ModalConfigEditor = ({ isDarkMode, onClose }) => {
  const [config, setConfig] = useState({
    title: "",
    message: "",
    image: null,
    showModal: true,
    delaySeconds: 5,
  })

  const [previewImage, setPreviewImage] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [removeImage, setRemoveImage] = useState(false)

  // Fetch current config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setIsLoading(true)
        const res = await API.get("/special-offer-modal")
        setConfig({
          title: res.data.title || "",
          message: res.data.message || "",
          image: null,
          showModal: res.data.showModal !== false,
          delaySeconds: res.data.delaySeconds || 5,
        })
        setPreviewImage(res.data.image || null)
      } catch (error) {
        console.error("Erreur chargement config modal:", error)
        alert("Erreur lors du chargement de la configuration")
      } finally {
        setIsLoading(false)
      }
    }

    fetchConfig()
  }, [])

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target

    if (name === "image" && files && files[0]) {
      const image = files[0]
      setConfig((prev) => ({ ...prev, image }))
      setRemoveImage(false)

      const reader = new FileReader()
      reader.onloadend = () => setPreviewImage(reader.result)
      reader.readAsDataURL(image)
    } else if (type === "checkbox") {
      setConfig((prev) => ({ ...prev, [name]: checked }))
    } else if (type === "number") {
      setConfig((prev) => ({ ...prev, [name]: Number(value) }))
    } else {
      setConfig((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleRemoveImage = () => {
    setRemoveImage(true)
    setPreviewImage(null)
    setConfig((prev) => ({ ...prev, image: null }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSaving(true)

    try {
      const form = new FormData()
      form.append("title", config.title)
      form.append("message", config.message)
      form.append("showModal", config.showModal)
      form.append("delaySeconds", config.delaySeconds)

      if (removeImage) {
        form.append("removeImage", "true")
      } else if (config.image) {
        form.append("image", config.image)
      }

      await API.put("/special-offer-modal", form, {
        headers: { "Content-Type": "multipart/form-data" },
      })

      alert("Configuration de la modal mise à jour avec succès")
      onClose()
    } catch (error) {
      console.error("Erreur mise à jour config:", error)
      alert("Erreur lors de la mise à jour de la configuration")
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className={isDarkMode ? "modal-overlay" : "light-modal-overlay"}>
        <div className={isDarkMode ? "modal-content" : "light-modal-content"}>
          <div className="loading-spinner">Chargement...</div>
        </div>
      </div>
    )
  }

  return (
    <div className={isDarkMode ? "modal-overlay" : "light-modal-overlay"} onClick={onClose}>
      <div className={isDarkMode ? "modal-content" : "light-modal-content"} onClick={(e) => e.stopPropagation()}>
        <div className={isDarkMode ? "modal-header" : "light-modal-header"}>
          <h2>Modifier l'affichage de la modal</h2>
          <button className={isDarkMode ? "modal-close" : "light-modal-close"} onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className={isDarkMode ? "modal-form" : "light-modal-form"}>
          <div className={isDarkMode ? "form-group" : "light-form-group"}>
            <label className={isDarkMode ? "form-label" : "light-form-label"}>Titre:</label>
            <input
              type="text"
              name="title"
              value={config.title}
              onChange={handleChange}
              placeholder="Titre de la modal"
              className={isDarkMode ? "form-input" : "light-form-input"}
              required
            />
          </div>

          <div className={isDarkMode ? "form-group" : "light-form-group"}>
            <label className={isDarkMode ? "form-label" : "light-form-label"}>Message:</label>
            <textarea
              name="message"
              value={config.message}
              onChange={handleChange}
              placeholder="Message de la modal"
              className={isDarkMode ? "form-input" : "light-form-input"}
              rows="4"
              required
            />
          </div>

          <div className={isDarkMode ? "form-group" : "light-form-group"}>
            <label className={isDarkMode ? "form-label" : "light-form-label"}>Image:</label>
            <CompressedFileInput
              type="file"
              name="image"
              accept="image/*"
              onChange={handleChange}
              className={isDarkMode ? "form-input" : "light-form-input"}
            />
            {previewImage && (
              <div className={isDarkMode ? "image-preview" : "light-image-preview"}>
                <img src={previewImage || "/placeholder.svg"} alt="Aperçu" />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className={isDarkMode ? "btn-remove-image" : "light-btn-remove-image"}
                >
                  Supprimer l'image
                </button>
              </div>
            )}
          </div>

          <div className={isDarkMode ? "form-group" : "light-form-group"}>
            <label className={isDarkMode ? "form-label" : "light-form-label"}>Délai d'affichage (secondes):</label>
            <input
              type="number"
              name="delaySeconds"
              value={config.delaySeconds}
              onChange={handleChange}
              min="0"
              max="60"
              className={isDarkMode ? "form-input" : "light-form-input"}
            />
          </div>

          <div className={isDarkMode ? "form-group checkbox-group" : "light-form-group light-checkbox-group"}>
            <label className={isDarkMode ? "checkbox-label" : "light-checkbox-label"}>
              <input
                type="checkbox"
                name="showModal"
                checked={config.showModal}
                onChange={handleChange}
                className={isDarkMode ? "form-checkbox" : "light-form-checkbox"}
              />
              <span>Afficher la modal</span>
            </label>
          </div>

          <div className={isDarkMode ? "modal-actions" : "light-modal-actions"}>
            <button
              type="submit"
              className={isDarkMode ? "btn btn-primary" : "light-btn light-btn-primary"}
              disabled={isSaving}
            >
              {isSaving ? "Enregistrement..." : "✅ Enregistrer"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className={isDarkMode ? "btn btn-secondary" : "light-btn light-btn-secondary"}
              disabled={isSaving}
            >
              ❌ Annuler
            </button>
          </div>
        </form>

        <style jsx>{`
          .modal-overlay,
          .light-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 2000;
          }

          .modal-content,
          .light-modal-content {
            background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
            border: 1px solid #334155;
            border-radius: 16px;
            padding: 2rem;
            max-width: 500px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          }

          .light-modal-content {
            background: linear-gradient(135deg, #ffffff 0%, #fdf2f8 100%);
            border: 1px solid #e2e8f0;
          }

          .modal-header,
          .light-modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid #334155;
          }

          .light-modal-header {
            border-bottom: 1px solid #e2e8f0;
          }

          .modal-header h2,
          .light-modal-header h2 {
            margin: 0;
            font-size: 1.5rem;
            font-weight: 600;
            color: #f1f5f9;
          }

          .light-modal-header h2 {
            color: #1e293b;
          }

          .modal-close,
          .light-modal-close {
            background: none;
            border: none;
            font-size: 2rem;
            cursor: pointer;
            color: #94a3b8;
            transition: all 0.3s ease;
            padding: 0;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .modal-close:hover,
          .light-modal-close:hover {
            color: #ec4899;
            transform: rotate(90deg);
          }

          .light-modal-close {
            color: #64748b;
          }

          .light-modal-close:hover {
            color: #ec4899;
          }

          .modal-form,
          .light-modal-form {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
          }

          .form-group,
          .light-form-group {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }

          .form-label,
          .light-form-label {
            font-weight: 500;
            color: #cbd5e1;
            font-size: 0.95rem;
          }

          .light-form-label {
            color: #1e293b;
          }

          .form-input,
          .light-form-input {
            padding: 0.75rem 1rem;
            border: 2px solid #475569;
            border-radius: 8px;
            background: #0f172a;
            color: #f1f5f9;
            font-size: 1rem;
            transition: all 0.3s ease;
            font-family: inherit;
          }

          .light-form-input {
            background: #ffffff;
            border-color: #e2e8f0;
            color: #1e293b;
          }

          .form-input:focus,
          .light-form-input:focus {
            outline: none;
            border-color: #ec4899;
            box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.1);
          }

          .form-input::placeholder,
          .light-form-input::placeholder {
            color: #64748b;
          }

          .image-preview,
          .light-image-preview {
            margin-top: 1rem;
            text-align: center;
          }

          .image-preview img,
          .light-image-preview img {
            max-width: 100%;
            max-height: 200px;
            border-radius: 8px;
            margin-bottom: 0.5rem;
          }

          .btn-remove-image,
          .light-btn-remove-image {
            padding: 0.5rem 1rem;
            background: linear-gradient(45deg, #ef4444, #dc2626);
            color: white;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.3s ease;
            font-size: 0.9rem;
          }

          .btn-remove-image:hover,
          .light-btn-remove-image:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
          }

          .checkbox-group,
          .light-checkbox-group {
            flex-direction: row;
            align-items: center;
          }

          .checkbox-label,
          .light-checkbox-label {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            cursor: pointer;
            font-weight: 500;
            color: #cbd5e1;
          }

          .light-checkbox-label {
            color: #1e293b;
          }

          .form-checkbox,
          .light-form-checkbox {
            width: 18px;
            height: 18px;
            cursor: pointer;
            accent-color: #ec4899;
          }

          .modal-actions,
          .light-modal-actions {
            display: flex;
            gap: 1rem;
            margin-top: 1.5rem;
            padding-top: 1rem;
            border-top: 1px solid #334155;
          }

          .light-modal-actions {
            border-top: 1px solid #e2e8f0;
          }

          .btn,
          .light-btn {
            padding: 0.75rem 1.5rem;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.3s ease;
            font-weight: 500;
            font-size: 1rem;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            flex: 1;
            justify-content: center;
          }

          .btn-primary,
          .light-btn-primary {
            background: linear-gradient(45deg, #ec4899, #8b5cf6);
            color: white;
            box-shadow: 0 4px 12px rgba(236, 72, 153, 0.3);
          }

          .btn-primary:hover,
          .light-btn-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(236, 72, 153, 0.4);
            background: linear-gradient(45deg, #db2777, #7c3aed);
          }

          .btn-primary:disabled,
          .light-btn-primary:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }

          .btn-secondary,
          .light-btn-secondary {
            background: #334155;
            color: #cbd5e1;
            border: 1px solid #475569;
          }

          .light-btn-secondary {
            background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
            color: #64748b;
            border: 2px solid #e2e8f0;
          }

          .btn-secondary:hover,
          .light-btn-secondary:hover {
            background: linear-gradient(45deg, #ec4899, #8b5cf6);
            color: white;
            border-color: transparent;
            transform: translateY(-2px);
          }

          .btn-secondary:disabled,
          .light-btn-secondary:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }

          .loading-spinner {
            text-align: center;
            color: #cbd5e1;
            padding: 2rem;
          }

          @media (max-width: 600px) {
            .modal-content,
            .light-modal-content {
              width: 95%;
              padding: 1.5rem;
            }

            .modal-header h2,
            .light-modal-header h2 {
              font-size: 1.25rem;
            }

            .modal-actions,
            .light-modal-actions {
              flex-direction: column;
            }

            .btn,
            .light-btn {
              width: 100%;
            }
          }
        `}</style>
      </div>
    </div>
  )
}

export default ModalConfigEditor
